import { cloneElement, isValidElement } from "react";
import { cn } from "@/lib/utils";

/**
 * Minimal form primitives — Field wraps a label + input/select.
 * shadcn-ui can be added later if we want unstyled accessible primitives.
 */

interface FieldProps {
  label: React.ReactNode;
  hint?: string;
  /** When true: shows the red asterisk AND propagates the HTML `required`
   *  attribute to a single child input/select so native browser validation
   *  fires on form submit. */
  required?: boolean;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

export function Field({ label, hint, required, error, className, children }: FieldProps) {
  // Propagate required down to a single child input/select. We avoid
  // doing this for non-HTML-input children (e.g. the YesNo button-pair):
  // the cloneElement call still works but the attribute is harmless on
  // a div, so we let it through unconditionally — keeping the helper
  // tiny is more important than typing perfection.
  const child = required && isValidElement(children)
    ? cloneElement(children as React.ReactElement, { required: true })
    : children;

  return (
    <label className={cn("block", className)}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      <div className="mt-1.5">{child}</div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </label>
  );
}

const inputBase =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed disabled:border-slate-200";

export const TextInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    type={props.type ?? "text"}
    className={cn(inputBase, props.className)}
  />
);

export const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...props} className={cn(inputBase, "bg-white", props.className)} />
);

/**
 * YesNo — button-pair for tri-state boolean answers.
 *
 * `value` is `boolean | null` because "not yet answered" is meaningful:
 * the form-mapping layer leaves the corresponding PDF radio blank
 * when no answer has been persisted, but writes /Y or /N once one is.
 * Pass `labels` to override the default "Yes" / "No" wording.
 */
export function YesNo({
  value,
  onChange,
  labels,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  labels?: { yes: string; no: string };
}) {
  const yesLabel = labels?.yes ?? "Yes";
  const noLabel = labels?.no ?? "No";
  return (
    <div className="flex gap-3">
      {[
        { v: true, label: yesLabel },
        { v: false, label: noLabel },
      ].map(({ v, label }) => {
        const selected = value === v;
        return (
          <button
            key={label}
            type="button"
            onClick={() => onChange(v)}
            className={cn(
              "px-5 py-2 rounded-lg border text-sm font-medium transition-colors",
              selected
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400",
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
